import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Users, Briefcase, FileText, TrendingUp, Plus, Pencil, Inbox, Settings, ClipboardList, BarChart3 } from 'lucide-react';
import api from '../utils/api';
import { getUser, getToken } from '../utils/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const token = getToken();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [resumeSubmissions, setResumeSubmissions] = useState([]);
  const [publicApplications, setPublicApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, jobsRes, appsRes, resumeRes, publicAppsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/jobs'),
        api.get('/admin/applications'),
        api.get('/admin/resume-submissions').catch(() => ({ data: [] })),
        api.get('/admin/public-applications').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setJobs(jobsRes.data);
      setApplications(appsRes.data);
      setResumeSubmissions(resumeRes.data);
      setPublicApplications(publicAppsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-32 px-6 flex items-center justify-center" data-testid="loading-state">
        <div className="text-lg text-zinc-500">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-32 px-6 md:px-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-extrabold text-zinc-900 mb-2" data-testid="dashboard-heading">Admin Dashboard</h1>
            <p className="text-lg text-zinc-500">Overview of platform activity</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/admin/analytics">
              <Button variant="outline" className="gap-2" data-testid="view-analytics-btn">
                <BarChart3 className="w-4 h-4" />
                Analytics
              </Button>
            </Link>
            <Link to="/admin/public-applications">
              <Button variant="outline" className="gap-2" data-testid="view-public-apps-btn">
                <ClipboardList className="w-4 h-4" />
                Job Applications ({publicApplications.length})
              </Button>
            </Link>
            <Link to="/admin/resume-submissions">
              <Button variant="outline" className="gap-2" data-testid="view-resumes-btn">
                <Inbox className="w-4 h-4" />
                Resume Submissions ({resumeSubmissions.length})
              </Button>
            </Link>
            <Link to="/admin/settings">
              <Button variant="outline" className="gap-2" data-testid="settings-btn">
                <Settings className="w-4 h-4" />
                Settings
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-zinc-200" data-testid="stat-total-users">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-zinc-500 uppercase tracking-wide">Total Users</p>
                <p className="text-3xl font-extrabold text-blue-900 font-accent">{stats.total_users}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-900" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-zinc-200" data-testid="stat-job-seekers">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-zinc-500 uppercase tracking-wide">Job Seekers</p>
                <p className="text-3xl font-extrabold text-blue-900 font-accent">{stats.total_seekers}</p>
              </div>
              <div className="w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-900" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-zinc-200" data-testid="stat-employers">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-zinc-500 uppercase tracking-wide">Employers</p>
                <p className="text-3xl font-extrabold text-blue-900 font-accent">{stats.total_employers}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-zinc-200" data-testid="stat-total-jobs">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-zinc-500 uppercase tracking-wide">Total Jobs</p>
                <p className="text-3xl font-extrabold text-blue-900 font-accent">{stats.total_jobs}</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-zinc-200" data-testid="stat-active-jobs">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-zinc-500 uppercase tracking-wide">Active Jobs</p>
                <p className="text-3xl font-extrabold text-blue-900 font-accent">{stats.active_jobs}</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-zinc-200" data-testid="stat-applications">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-zinc-500 uppercase tracking-wide">Applications</p>
                <p className="text-3xl font-extrabold text-blue-900 font-accent">{stats.total_applications}</p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="mb-6" data-testid="admin-tabs">
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="jobs-tab">Jobs</TabsTrigger>
            <TabsTrigger value="applications" data-testid="applications-tab">Applications</TabsTrigger>
          </TabsList>

          <TabsContent value="users" data-testid="users-content">
            <div className="bg-white rounded-3xl p-8 border border-zinc-200">
              <h2 className="text-2xl font-bold text-zinc-900 mb-6">All Users</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-200">
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Role</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Company</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-zinc-100" data-testid={`user-row-${u.id}`}>
                        <td className="py-3 px-4">{u.name}</td>
                        <td className="py-3 px-4 text-zinc-600">{u.email}</td>
                        <td className="py-3 px-4">
                          <span className="px-3 py-1 bg-blue-50 text-blue-900 text-xs font-semibold rounded-full">
                            {u.role}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-zinc-600">{u.company_name || '-'}</td>
                        <td className="py-3 px-4 text-zinc-600">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="jobs" data-testid="jobs-content">
            <div className="bg-white rounded-3xl p-8 border border-zinc-200">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-zinc-900">All Jobs</h2>
                <Button
                  onClick={() => navigate('/admin/jobs/new')}
                  className="bg-brand-blue text-white font-semibold rounded-xl px-6 hover:bg-brand-blue/90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Job
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-200">
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Title</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Company</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Location</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Type</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Posted</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr key={job.id} className="border-b border-zinc-100" data-testid={`job-row-${job.id}`}>
                        <td className="py-3 px-4 font-semibold">{job.title}</td>
                        <td className="py-3 px-4 text-zinc-600">{job.company}</td>
                        <td className="py-3 px-4 text-zinc-600">{job.location}</td>
                        <td className="py-3 px-4">
                          <span className="px-3 py-1 bg-zinc-100 text-zinc-700 text-xs font-semibold rounded-full capitalize">
                            {job.job_type}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            job.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-zinc-100 text-zinc-700'
                          }`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-zinc-600">
                          {new Date(job.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/jobs/${job.id}/edit`)}
                            className="text-brand-blue hover:bg-brand-blue/10"
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="applications" data-testid="applications-content">
            <div className="bg-white rounded-3xl p-8 border border-zinc-200">
              <h2 className="text-2xl font-bold text-zinc-900 mb-6">All Applications</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-200">
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Applicant</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Job Title</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Company</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-zinc-700">Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.id} className="border-b border-zinc-100" data-testid={`application-row-${app.id}`}>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-semibold">{app.applicant_name}</p>
                            <p className="text-sm text-zinc-500">{app.applicant_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-semibold">{app.job_title}</td>
                        <td className="py-3 px-4 text-zinc-600">{app.company}</td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            app.status === 'reviewed' ? 'bg-blue-100 text-blue-800' :
                            app.status === 'shortlisted' ? 'bg-lime-200 text-blue-900' :
                            app.status === 'accepted' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {app.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-zinc-600">
                          {new Date(app.applied_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}